import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Paperclip, X } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_BUG_SCREENSHOTS = 20;

export default function ReportIssue({ onBack }) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [bugDescription, setBugDescription] = useState('');
  const [bugScreenshots, setBugScreenshots] = useState([]);
  const [bugScreenshotNames, setBugScreenshotNames] = useState([]);
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const [bugSubmitted, setBugSubmitted] = useState(false);
  const bugFileRef = useRef(null);

  const readImageAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const MAX = 1280;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const handleBugScreenshot = async (e) => {
    const selected = Array.from(e.target.files || []).filter(f => f.type?.startsWith('image/'));
    e.target.value = '';
    if (selected.length === 0) return;
    const availableSlots = MAX_BUG_SCREENSHOTS - bugScreenshots.length;
    if (availableSlots <= 0) { alert(`You can attach up to ${MAX_BUG_SCREENSHOTS} screenshots per bug.`); return; }
    const filesToAdd = selected.slice(0, availableSlots);
    if (selected.length > filesToAdd.length) alert(`Only ${MAX_BUG_SCREENSHOTS} screenshots are allowed per bug.`);
    try {
      const newShots = await Promise.all(filesToAdd.map(readImageAsDataUrl));
      setBugScreenshots(prev => [...prev, ...newShots]);
      setBugScreenshotNames(prev => [...prev, ...filesToAdd.map(f => f.name)]);
    } catch { alert('Some screenshots could not be loaded. Please try again.'); }
  };

  const removeBugScreenshotAt = (index) => {
    setBugScreenshots(prev => prev.filter((_, i) => i !== index));
    setBugScreenshotNames(prev => prev.filter((_, i) => i !== index));
  };

  const submitBug = async () => {
    if (!bugDescription.trim()) return;
    setBugSubmitting(true);
    try {
      await axios.post(`${API_URL}/bugs`, {
        description: bugDescription,
        screenshot: bugScreenshots[0] || null,
        screenshots: bugScreenshots,
      });
      setBugSubmitted(true);
      setBugDescription('');
      setBugScreenshots([]);
      setBugScreenshotNames([]);
    } catch (e) {
      alert('Failed to submit. Try again.');
    } finally {
      setBugSubmitting(false);
    }
  };

  return (
    <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button onClick={() => { onBack(); setBugSubmitted(false); }} className="app-card p-1.5 rounded-md" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
                  <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
                </button>
                <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>Report Issue</h1>
              </div>
              <button onClick={() => navigate('/bug-reports')} className="text-[13px] font-medium" style={{ color: colors.accent }}>
                View all →
              </button>
            </div>

            {bugSubmitted ? (
              <div className="app-card rounded-lg p-6 text-center mt-4" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
                <CheckCircle className="w-10 h-10 mx-auto mb-3" style={{ color: colors.profit }} />
                <p className="text-[14px] font-semibold mb-1" style={{ color: colors.primaryText }}>Bug reported!</p>
                <p className="text-[13px] mb-4" style={{ color: colors.secondaryText }}>Thanks — we'll look into it.</p>
                <button
                  onClick={() => setBugSubmitted(false)}
                  className="px-4 py-2 rounded-lg text-[13px] font-semibold"
                  style={{ backgroundColor: colors.accent, color: '#fff' }}
                >
                  Report another
                </button>
              </div>
            ) : (
              <div className="app-card rounded-lg p-4 space-y-4 mt-4" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
                {/* Description */}
                <div>
                  <p className="text-[13px] font-medium mb-2" style={{ color: colors.secondaryText }}>What's wrong? *</p>
                  <textarea
                    rows={6}
                    placeholder="Describe the issue — what happened, what page, what you expected..."
                    value={bugDescription}
                    onChange={e => setBugDescription(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg text-[14px] resize-none focus:outline-none"
                    style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderWidth: '1px', borderColor: colors.border }}
                  />
                </div>

                {/* Screenshot upload */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[13px] font-medium" style={{ color: colors.secondaryText }}>Screenshots (optional)</p>
                    <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                      {bugScreenshots.length}/{MAX_BUG_SCREENSHOTS}
                    </p>
                  </div>
                  <input ref={bugFileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleBugScreenshot} />

                  <button
                    onClick={() => bugFileRef.current?.click()}
                    className="w-full py-2.5 rounded-lg border-dashed border-2 flex items-center justify-center gap-2 text-[13px]"
                    style={{ borderColor: colors.border, color: colors.secondaryText }}
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    {bugScreenshots.length ? 'Add more screenshots' : 'Attach screenshots (up to 20)'}
                  </button>

                  {bugScreenshots.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {bugScreenshots.map((shot, idx) => (
                        <div key={`bug-shot-${idx}`} className="relative rounded-lg border p-2" style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}>
                          <img src={shot} alt={`screenshot ${idx + 1}`} className="w-full rounded-md h-28 object-contain" />
                          <button
                            onClick={() => removeBugScreenshotAt(idx)}
                            className="absolute top-2 right-2 p-1 rounded-full"
                            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                            type="button"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                          {bugScreenshotNames[idx] && (
                            <p className="mt-1 text-[11px] truncate" style={{ color: colors.secondaryText }}>
                              {bugScreenshotNames[idx]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  onClick={submitBug}
                  disabled={!bugDescription.trim() || bugSubmitting}
                  className="w-full py-2.5 rounded-lg text-[14px] font-semibold transition-opacity"
                  style={{
                    backgroundColor: colors.accent,
                    color: '#fff',
                    opacity: (!bugDescription.trim() || bugSubmitting) ? 0.5 : 1
                  }}
                >
                  {bugSubmitting ? 'Submitting...' : 'Submit Bug Report'}
                </button>
              </div>
            )}
    </div>
  );
}
